import { Link } from "react-router-dom";
import { GraduationCap, Award, CalendarCheck, BadgeCheck, Stethoscope } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { Reveal } from "../components/motion";
import { PhotoSlot } from "../components/PhotoSlot";
import { Marquee } from "../components/Marquee";
import { PatientJourney } from "../components/sections";
import { DOCTOR, CLINIC } from "../lib/constants";

const QUALIFICATIONS = [
  { degree: "MBBS", detail: "Bachelor of Medicine, Bachelor of Surgery" },
  { degree: "MD", detail: "Doctor of Medicine — Ophthalmology" },
  { degree: "FAICO", detail: "Fellowship of the All India Collegium of Ophthalmology — Retina & Vitreous" },
];

const About = () => (
  <>
    <PageHero
      kicker="About Your Doctor"
      testid="about-hero"
      lines={[
        <>Specialist care,</>,
        <>
          led by <span className="font-serif italic font-semibold text-brand-bright">experience</span>.
        </>,
      ]}
      sub="Retina, Cataract, Uvea, ROP & Vitreoretinal Diseases — Janak Puri, New Delhi."
    />

    <section className="py-20 sm:py-28" data-testid="doctor-profile-section">
      <div className="container-x grid gap-14 lg:grid-cols-[0.9fr_1.1fr] items-start">
        <Reveal className="lg:sticky lg:top-28">
          <div className="relative max-w-md mx-auto lg:mx-0">
            <div className="absolute -inset-4 rounded-t-[999px] rounded-b-[2rem] bg-brand-mist" aria-hidden="true" />
            <PhotoSlot
              dark
              label="Dr. S.J. Vikas"
              note="MBBS, MD, FAICO — AIIMS, New Delhi"
              src="/images/doctor-portrait.jpg"
              alt="Dr. S.J. Vikas, ophthalmologist and eye surgeon, iNetra Janak Puri"
              className="relative aspect-[4/5] rounded-t-[999px] rounded-b-[2rem] shadow-2xl shadow-blue-900/25"
            />
            <div className="absolute -right-3 sm:-right-6 bottom-16 rounded-2xl bg-white shadow-xl border border-slate-100 px-4 py-3 animate-float-slow" data-testid="about-chip-surgeries">
              <p className="text-lg font-extrabold text-ink leading-none">10,000+</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Surgeries</p>
            </div>
          </div>
        </Reveal>

        <div>
          <SectionHead
            chapter="01"
            kicker="Profile"
            title={DOCTOR.name}
            sub={`${DOCTOR.degrees} — ${DOCTOR.role}`}
          />
          <Reveal delay={0.1} className="mt-6 flex flex-wrap gap-2.5">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-mist px-4 py-2 text-xs font-bold text-ink">
              <GraduationCap className="w-3.5 h-3.5 text-brand-bright" aria-hidden="true" /> {DOCTOR.training}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-aqua-mist px-4 py-2 text-xs font-bold text-ink">
              <Award className="w-3.5 h-3.5 text-aqua" aria-hidden="true" /> FAICO — Retina & Vitreous
            </span>
          </Reveal>
          <Reveal delay={0.18}>
            <blockquote className="mt-8 rounded-2xl border-l-4 border-aqua bg-slate-50 p-7 text-base sm:text-lg leading-relaxed text-slate-700 font-medium" data-testid="doctor-bio">
              “{DOCTOR.bio}”
            </blockquote>
          </Reveal>
          <Reveal delay={0.24} className="mt-8">
            <dl className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <dt className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-aqua" aria-hidden="true" /> Specialist in
                </dt>
                <dd className="mt-2 text-sm font-bold text-ink leading-relaxed">{DOCTOR.specialties}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <dt className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-aqua" aria-hidden="true" /> Experience
                </dt>
                <dd className="mt-2 text-sm font-bold text-ink leading-relaxed">More than 10 years in ophthalmology and eye surgery</dd>
              </div>
            </dl>
          </Reveal>
          <Reveal delay={0.3} className="mt-8">
            <Link to="/contact#book" className="btn-primary" data-testid="about-book-button">
              <CalendarCheck className="w-4 h-4" aria-hidden="true" /> Book a Consultation
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 bg-slate-50" data-testid="qualifications-section">
      <div className="container-x">
        <SectionHead
          chapter="02"
          kicker="Credentials & Recognition"
          title="Qualifications you can verify"
          sub="Formal training and recognised fellowship credentials — nothing more claimed, nothing less delivered."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {QUALIFICATIONS.map((q, i) => (
            <Reveal key={q.degree} delay={i * 0.08}>
              <div className="card-lift h-full rounded-2xl border border-slate-200 bg-white p-7" data-testid={`qualification-${q.degree.toLowerCase()}`}>
                <p className="font-serif italic text-3xl text-brand-bright">{q.degree}</p>
                <p className="mt-3 text-sm font-semibold text-ink leading-relaxed">{q.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-ink text-white noise-dark relative overflow-hidden p-7">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-aqua-bright">Training</p>
              <p className="relative mt-2 text-lg font-extrabold">{DOCTOR.training}</p>
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="rounded-2xl border border-aqua/25 bg-aqua-mist p-7">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-aqua">Fellowship</p>
              <p className="mt-2 text-lg font-extrabold text-ink">{DOCTOR.faico}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28" data-testid="recognition-section">
      <div className="container-x">
        <SectionHead
          chapter="03"
          kicker="Recognition"
          title="Recognised expertise in vitreo-retinal surgery"
          sub="Credentials and recognition from the All India Institute of Medical Sciences and the All India Collegium of Ophthalmology."
        />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 max-w-4xl">
          <Reveal>
            <PhotoSlot
              label="AIIMS, New Delhi"
              note="MD (Ophthalmology) · December 2017"
              src="/images/aiims-md-certificate.webp"
              fit="contain"
              alt="AIIMS New Delhi MD Ophthalmology degree certificate of Dr. S.J. Vikas, December 2017"
              className="rounded-3xl border border-slate-200 shadow-xl shadow-blue-900/5"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <PhotoSlot
              label="FAICO — Retina & Vitreous"
              note="All India Collegium of Ophthalmology · June 2021"
              src="/images/faico-certificate.webp"
              fit="contain"
              alt="FAICO fellowship certificate awarded to Dr. S.J. Vikas in Retina and Vitreous, June 2021"
              className="rounded-3xl border border-slate-200 shadow-xl shadow-blue-900/5"
            />
          </Reveal>
        </div>
        <Reveal delay={0.15} className="mt-4">
          <PhotoSlot
            label="AIIMS Convocation, New Delhi"
            note="Dr. S.J. Vikas receiving his MD (Ophthalmology) degree · 2018"
            src="/images/aiims-convocation.webp"
            alt="Dr. S.J. Vikas receiving his MD Ophthalmology degree at the AIIMS New Delhi annual convocation, 2018"
            className="w-full aspect-[4/3] sm:aspect-[3/2] rounded-3xl shadow-xl shadow-blue-900/10"
          />
        </Reveal>
      </div>
    </section>

    <Marquee />

    <section className="py-20 sm:py-28 bg-slate-50">
      <div className="container-x">
        <SectionHead
          chapter="04"
          kicker="Your Visit"
          title="Your eye care, made simple"
          sub="What to expect when you visit iNetra for the first time."
        />
        <div className="mt-12">
          <PatientJourney />
        </div>
      </div>
    </section>
  </>
);

export default About;
